import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaShieldAlt, FaCheck, FaClock, FaUsers, FaLightbulb, FaBookOpen, FaHandsHelping, FaExclamationTriangle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import lymeDiseaseImage from '../../components/assets/Staff/Lyme Disease1.jpg';

const LymeDisease = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const understandingRef = useRef(null);
  const complementaryRef = useRef(null);
  const scientificRef = useRef(null);
  const summaryRef = useRef(null);
  const reminderRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'understanding': understandingRef,
      'complementary': complementaryRef,
      'scientific': scientificRef,
      'summary': summaryRef,
      'reminder': reminderRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'understanding', ref: understandingRef },
        { id: 'complementary', ref: complementaryRef },
        { id: 'scientific', ref: scientificRef },
        { id: 'summary', ref: summaryRef },
        { id: 'reminder', ref: reminderRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'understanding', label: 'Understanding', icon: FaBookOpen },
    { id: 'complementary', label: 'Complementary Support', icon: FaHandsHelping },
    { id: 'scientific', label: 'Scientific Context', icon: FaLightbulb },
    { id: 'summary', label: 'In Summary', icon: FaCheck },
    { id: 'reminder', label: 'Important Notes', icon: FaExclamationTriangle }
  ];

  const complementaryBullets = [
    "experience ongoing fatigue, discomfort or reduced resilience",
    "feel overwhelmed by long-term symptoms and uncertainty",
    "want gentle, non-invasive support alongside medical care",
    "are looking for a personalised, whole-person wellbeing approach"
  ];

  const resourceLinks = [
    { name: "NHS – Lyme disease overview", url: "https://www.nhs.uk/conditions/lyme-disease/" },
    { name: "CDC – Lyme disease information and guidance", url: "https://www.cdc.gov/lyme/index.html" },
    { name: "Healthline – overview of bioresonance and current research context", url: "https://www.healthline.com/health/bioresonance#research" },
    { name: "The Open Epidemiology Journal (2020) – complementary bioresonance research overview", url: "https://benthamopen.com/FULLTEXT/TOEPIJ-8-1" }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance for Lyme Disease" description="Bioresonance support for Lyme-related symptoms at Bioresonance Leicester. Gentle, complementary approach. Book at 108 New Walk." path="/therapy/bioresonance-therapy/lyme-disease" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy/bioresonance-therapy" className="hover:text-gold transition-colors">Bioresonance Therapy</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Lyme Disease</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                Bioresonance Sub-Treatment
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Lyme Disease
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Supporting wellbeing in the context of a complex, long-term condition
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Living with Lyme disease can be challenging, unpredictable and deeply exhausting. Many people experience ongoing symptoms that affect energy levels, concentration, emotional balance and overall quality of life, often long after the initial infection has been treated.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                For some individuals, this ongoing uncertainty leads them to explore complementary, non-invasive approaches that focus on supporting the whole person, alongside conventional medical care.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance is one such option, applied in practice as a gentle, frequency-based approach.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={lymeDiseaseImage}
                      alt="Lyme disease support"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section - anchor for nav */}
      <section ref={overviewRef} className="py-4" aria-hidden="true" />

      {/* Understanding Lyme disease from a holistic wellbeing perspective */}
      <section ref={understandingRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Understanding Lyme disease from a holistic wellbeing perspective
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Lyme disease is a bacterial infection caused by Borrelia burgdorferi, transmitted through the bite of infected ticks. Symptoms can vary widely between individuals and may affect multiple systems within the body.
              </p>
              <p>
                Early symptoms may include fatigue, headaches, joint discomfort or flu-like sensations. In some cases, people report ongoing or fluctuating symptoms that persist over time and significantly affect daily functioning.
              </p>
              <p>
                From a holistic wellbeing perspective, long-term Lyme-related symptoms are often viewed not as a single isolated issue, but as part of a broader interaction involving immune response, nervous system regulation, stress load, emotional resilience and overall balance.
              </p>
              <p>
                This perspective does not replace medical diagnosis or infectious disease management. It does not aim to explain Lyme disease clinically, but rather to support individuals living with its ongoing impact, alongside appropriate medical care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Complementary support in long-term and complex conditions */}
      <section ref={complementaryRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Complementary support in long-term and complex conditions
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed mb-8">
              <p>
                Within complementary wellness settings, this frequency-based method may be explored as a non-invasive option to support overall balance, stress regulation and individual wellbeing, particularly by those living with long-term or complex health challenges.
              </p>
              <p>
                Sessions are personalised and adapted to the individual's current state, health history and personal wellbeing goals. The approach is non-invasive and does not involve medication or physical manipulation.
              </p>
              <p className="font-semibold text-dark-grey">Some individuals choose to explore this form of complementary support when they:</p>
              <ul className="space-y-3 list-none">
                {complementaryBullets.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="text-gold mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                This frequency-based approach is generally used within a wellbeing-focused framework, drawing on practitioner experience and individual response rather than standardised treatment protocols.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific context and ongoing discussion */}
      <section ref={scientificRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Scientific context and ongoing discussion
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed mb-8">
              <p>
                Scientific research specifically addressing Lyme disease in relation to bioresonance remains limited. However, broader interest in this frequency-based method continues among individuals seeking gentle, holistic support for balance, stress regulation and resilience, alongside conventional medical care.
              </p>
              <p className="font-semibold text-dark-grey">Available information and background sources include:</p>
              <ul className="space-y-2">
                {resourceLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:text-gold/80 underline transition-colors"
                    >
                      {link.name}
                    </a>
                    <br />
                    <span className="text-sm text-gray-500 break-all">{link.url}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* In summary */}
      <section ref={summaryRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                In summary
              </h2>
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-gray-600 leading-relaxed">
                For individuals living with Lyme disease, bioresonance, applied as a frequency-based method, offers a gentle and non-invasive way to explore holistic wellbeing. With its personalised focus and emphasis on balance and resilience, it is increasingly chosen as a supportive addition to conventional care by those seeking a more integrated wellbeing experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Reminder */}
      <section ref={reminderRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center bg-blue/10 text-blue px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaExclamationTriangle className="mr-2" />
                Important Information
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Important Notes
              </h2>
            </div>
            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-gray-600 leading-relaxed">
                Bioresonance is intended as a complementary method and does not replace medical care. If you are undergoing conventional medical treatment for Lyme disease, do not stop or change your medications without first consulting your doctor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Lyme Disease Support?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Discover how bioresonance therapy can provide complementary support for managing Lyme disease symptoms and improving overall wellbeing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
            <Link 
              to="/therapy/bioresonance-therapy"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              Back to Bioresonance Therapy
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default LymeDisease;
